/**
 * Manual backup (FR-BAK-03, FR-BAK-04).
 *
 * Pulled forward from M9 into M5 deliberately (IMPLEMENTATION-PLAN §1.3): M5 is the
 * cutover, and the workbook stops being maintained at that point. Without this there
 * would be four milestones during which the entire financial record lived in one
 * database with no export path.
 *
 * The scheduled weekly backup and the Drive destination are in `server/` (M9); in-app
 * restore is at the foot of this file.
 */

import { collection, doc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import { RESTORABLE, restoreWrites, type Snapshot } from '../core/restore';
import { writeAudit } from './audit';
import { db } from './firebase';
import { trackWrite } from './syncState';

/** Every collection worth preserving. Rollups are omitted — they rebuild from these. */
const COLLECTIONS = [
  'accounts',
  'categories',
  'tags',
  'transactions',
  'settings',
  'users',
  'loans',
  'receivables',
  'recurring',
  'tasks',
  'budgets',
  'imports',
  'auditLog',
];

export interface BackupSnapshot {
  takenAt: string;
  appVersion: string;
  collections: Record<string, Record<string, unknown>[]>;
  counts: Record<string, number>;
}

/**
 * Read everything into one JSON document.
 *
 * Timestamps are converted to ISO strings rather than left as Firestore objects, so the
 * file is readable and restorable without the SDK — a backup that needs the app to make
 * sense of it is not much of a backup.
 */
export async function buildSnapshot(): Promise<BackupSnapshot> {
  const collections: BackupSnapshot['collections'] = {};
  const counts: Record<string, number> = {};

  for (const name of COLLECTIONS) {
    try {
      const snapshot = await getDocs(collection(db, name));
      collections[name] = snapshot.docs.map((d) => ({
        id: d.id,
        ...(normalise(d.data()) as Record<string, unknown>),
      }));
      counts[name] = snapshot.size;
    } catch {
      // A collection the current user cannot read is recorded as absent rather than
      // failing the whole backup — a partial snapshot beats none.
      collections[name] = [];
      counts[name] = -1;
    }
  }

  return {
    takenAt: new Date().toISOString(),
    appVersion: 'M9',
    collections,
    counts,
  };
}

function normalise(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  const stamp = value as { toDate?: () => Date };
  if (typeof stamp.toDate === 'function') return stamp.toDate().toISOString();
  if (Array.isArray(value)) return value.map(normalise);
  if (typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, normalise(v)]));
  }
  return value;
}

/** Serialise and hand the browser a download. */
export async function downloadBackup(): Promise<{ fileName: string; counts: Record<string, number> }> {
  const snapshot = await buildSnapshot();
  const fileName = `expenses-backup-${snapshot.takenAt.slice(0, 19).replace(/[:T]/g, '-')}.json`;

  const blob = new Blob([JSON.stringify(snapshot, null, 1)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);

  return { fileName, counts: snapshot.counts };
}

// ---------------------------------------------------------------------------
// Restore (FR-BAK-05, FR-BAK-09)
// ---------------------------------------------------------------------------

/**
 * Read every restorable collection, keyed by collection then id.
 *
 * The shape `previewRestore` wants. Read once and used for both the preview and the
 * write, so the two cannot disagree about what was there.
 */
export async function readLive(): Promise<Record<string, Record<string, Record<string, unknown>>>> {
  const live: Record<string, Record<string, Record<string, unknown>>> = {};

  for (const name of RESTORABLE) {
    try {
      const snapshot = await getDocs(collection(db, name));
      live[name] = Object.fromEntries(
        snapshot.docs.map((d) => [d.id, normalise(d.data()) as Record<string, unknown>]),
      );
    } catch {
      live[name] = {};
    }
  }

  return live;
}

export interface RestoreResult {
  written: number;
  monthsToRebuild: string[];
}

/**
 * Perform a restore (FR-BAK-05, FR-BAK-09).
 *
 * Writes only what the preview said would change — same function, same inputs — and
 * **merges rather than replaces**, so a field added since the backup survives instead of
 * being silently dropped.
 *
 * Rollups are deliberately not restored, so the transactions may now disagree with them.
 * The months touched come back in the result and the caller rebuilds them; leaving that
 * to a nightly job would mean a wrong dashboard until then.
 */
export async function performRestore(
  snapshot: Snapshot,
  live: Record<string, Record<string, Record<string, unknown>>>,
  by: string,
): Promise<RestoreResult> {
  const writes = restoreWrites(snapshot, live);
  const months = new Set<string>();

  const CHUNK = 200;
  for (let i = 0; i < writes.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const write of writes.slice(i, i + CHUNK)) {
      batch.set(doc(db, write.collection, write.id), { ...write.data, restoredAt: serverTimestamp() }, { merge: true });
      if (write.collection === 'transactions' && typeof write.data.monthKey === 'string') {
        months.add(write.data.monthKey);
      }
    }
    await trackWrite(batch.commit());
  }

  // FR-BAK-09: a restore is written to the audit log. One entry for the operation, not
  // per record — this is one deliberate act, unlike a bulk reclassify where each row is
  // an independent change the user chose.
  await writeAudit({
    action: 'update',
    collection: 'backup',
    documentId: snapshot.takenAt,
    changes: [
      { field: 'restoredFrom', before: null, after: snapshot.takenAt },
      { field: 'recordsWritten', before: null, after: writes.length },
    ],
    by,
    context: 'restore',
  });

  return { written: writes.length, monthsToRebuild: [...months].sort() };
}
